import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FiUsers, FiAlertCircle, FiCheck, FiSend, FiSearch, FiArrowRight, FiSliders, FiMessageCircle } from 'react-icons/fi';
import ShieldCheckIcon from '../ShieldCheckIcon';
import api from '../../../api/api';
import { startSetStock } from '../../../actions/CurrentStock';
import { toast } from '../Toast';
import { CURRENT_TICKER_KEY, initials } from '../Header';
import useCarousel from '../useCarousel';
import AccountNav from './AccountNav';

// The client list is real (existing GET /api/clientDashboard, same as the header switcher). Plan, health checks,
// last activity and email stats have no source yet, so they show "—"; health is only "Setting up" (no registry) or "Connected".
interface Client { id: string; name: string; ticker: string; registry: string | null }
type Health = 'all' | 'attention' | 'good' | 'setup';

const PAGE_SIZE = 8;

const healthOf = (c: Client): Health => c.registry ? 'good' : 'setup';

const FILTERS: { key: Health; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'attention', label: 'Need attention' },
    { key: 'good', label: 'All good' },
    { key: 'setup', label: 'Setting up' },
];

const MyClients: React.FC = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [clients, setClients] = useState<Client[] | null>(null);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<Health>('all');
    const [page, setPage] = useState(0);

    useEffect(() => {
        api().getClientDashboardResult().then(r => setClients(r.stocks
            .map(s => ({ id: s.id, name: s.companyName || s.combinedTicker, ticker: s.combinedTicker, registry: s.registry }))
            .sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() => setLoadError('Could not load your clients'));
    }, []);

    const all = clients ?? [];
    const count = (h: Health) => h === 'all' ? all.length : all.filter(c => healthOf(c) === h).length;
    const q = search.trim().toLowerCase();
    const filtered = all
        .filter(c => filter === 'all' || healthOf(c) === filter)
        .filter(c => !q || c.name.toLowerCase().includes(q) || c.ticker.toLowerCase().includes(q));
    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const shown = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    const connected = count('good');
    const settingUp = all.filter(c => !c.registry);

    // same as picking the client in the header switcher
    const open = (c: Client) => {
        localStorage.setItem(CURRENT_TICKER_KEY, c.ticker);
        dispatch(startSetStock(c.ticker));
        history.push('/dashboard/overview');
    };

    // stats carousel on tablet / mobile (grid on desktop, where count stays 1 and no dots show)
    const stats = useCarousel<HTMLDivElement>([clients]);

    const changeFilter = (f: Health) => { setFilter(f); setPage(0); };
    const askInvesti = () => toast.info('Preview only: ask your Investi account manager to add a client for now');

    return <>
        <div className="page-header">
            <div>
                <h1>My Clients</h1>
                <p>The {all.length || ''} companies you manage in Investi · Investi adds and removes clients for you, everything else is yours to run</p>
            </div>
            <div className="action-top">
                <button type="button" className="btn-outline with-icon" onClick={askInvesti}><FiMessageCircle /> Ask Investi to Add a Client</button>
            </div>
        </div>

        <div className="account-row">
            <AccountNav />

            <div className="account-main">
                <div className="banner">
                    <span className="icon-tile"><ShieldCheckIcon /></span>
                    <div>
                        <div className="title">Clients are added and removed by Investi</div>
                        <div className="hint">Ask your Investi account manager (or use the chat) to add a new client, change a company name or close an account. You can open, set up and run every client listed here.</div>
                    </div>
                </div>

                {loadError && <p className="error">{loadError}</p>}
                {clients && <>
                    {/* grid on desktop; swipeable carousel on tablet (2 per view) / mobile (1 per view) with dots (Account.scss) */}
                    <div className="stats" ref={stats.ref} onScroll={stats.onScroll}>
                        <div className="stat card">
                            <div className="stat-head"><span className="icon-tile"><FiUsers /></span><span>Client companies</span></div>
                            <div className="value">{all.length}</div>
                            <div className="hint">Plans not available yet</div>
                        </div>
                        <div className="stat card">
                            <div className="stat-head"><span className="icon-tile"><FiAlertCircle /></span><span>Need attention</span></div>
                            <div className="value">—</div>
                            <div className="hint">Approvals waiting or widgets missing</div>
                        </div>
                        <div className="stat card">
                            <div className="stat-head"><span className="icon-tile"><FiCheck /></span><span>Registries connected</span></div>
                            <div className="value">{connected} of {all.length}</div>
                            <div className="hint">{settingUp.length === 0 ? 'Every client is connected' : settingUp.length === 1 ? `${settingUp[0].name} is still setting up` : `${settingUp.length} clients are still setting up`}</div>
                        </div>
                        <div className="stat card">
                            <div className="stat-head"><span className="icon-tile"><FiSend /></span><span>Emails sent this month</span></div>
                            <div className="value">—</div>
                            <div className="hint">Across all clients</div>
                        </div>
                    </div>
                    {stats.count > 1 && <div className="stats-dots">
                        {Array.from({ length: stats.count }, (_, i) => <button key={i} type="button" aria-label={`Show stats ${i + 1}`}
                            className={i === stats.index ? 'active' : ''} onClick={() => stats.goTo(i)} />)}
                    </div>}

                    <section className="card clients">
                        <div className="toolbar">
                            <div className="search"><FiSearch /><input placeholder="Search by name or ASX code" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} /></div>
                            <div className="chips">
                                {FILTERS.map(f => <button key={f.key} type="button" className={filter === f.key ? 'active' : ''} onClick={() => changeFilter(f.key)}>
                                    {f.label} <span className="n">{count(f.key)}</span>
                                </button>)}
                            </div>
                        </div>

                        <div className="table">
                            <div className="tr th">
                                <span className="client">Client</span><span className="plan">Plan</span><span className="health">Health</span><span className="activity">Last activity</span><span className="actions" />
                            </div>
                            {shown.map(c => <div key={c.id} className="tr">
                                {/* the name + mark also open the client (the only way on mobile, where the Open button is hidden) */}
                                <button type="button" className="client" onClick={() => open(c)} title={`Open ${c.name}`}>
                                    <span className="mark">{initials(c.name)}</span>
                                    <span className="text"><strong>{c.name}</strong><small>{c.ticker}{c.registry && ` · ${c.registry}`}</small></span>
                                </button>
                                <span className="plan muted">—</span>
                                <span className="health">{c.registry ? <span className="badge ok">Connected</span> : <span className="badge info">Setting up</span>}</span>
                                <span className="activity muted">—</span>
                                <span className="actions">
                                    <button type="button" className="btn-outline with-icon" onClick={() => open(c)}><FiArrowRight /> Open</button>
                                    <button type="button" className="icon-btn" title="Client settings" aria-label={`${c.name} settings`}
                                        onClick={() => toast.info('Preview only: client settings aren’t built yet')}><FiSliders /></button>
                                </span>
                            </div>)}
                            {shown.length === 0 && <p className="empty">No clients match</p>}
                        </div>

                        <div className="pager">
                            <span>Showing {shown.length} of {filtered.length} clients</span>
                            <button type="button" className="btn-outline" onClick={() => setPage(page - 1)} disabled={page === 0}>Previous</button>
                            <button type="button" className="btn-outline" onClick={() => setPage(page + 1)} disabled={page >= pages - 1}>Next</button>
                        </div>
                    </section>
                </>}

                {/* mobile: moves from the header to the end of the page (Account.scss .action-top / .action-bottom) */}
                <div className="action-bottom">
                    <button type="button" className="btn-outline with-icon" onClick={askInvesti}><FiMessageCircle /> Ask Investi to Add a Client</button>
                </div>
            </div>
        </div>
    </>;
};

export default MyClients;
